import React, { useState, useEffect, Ref } from 'react';
import DropdownArrow from '@/assets/caretDownLight.svg';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  useFloating,
  autoUpdate,
  offset as _offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useFocus,
  useInteractions,
  FloatingFocusManager,
  Placement,
  FloatingPortal,
} from '@floating-ui/react';

// https://ariakit.org/examples/menu-framer-motion

export interface PopoverMenuMethods {
  open: () => void;
  close: () => void;
}

interface IPopoverMenu {
  title: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
  titleClass?: string;
  offset?: number;
  placement?: Placement;
  contentWrapperClassName?: string;
  contentStyle?: React.CSSProperties;
  portal?: boolean;
  disabled?: boolean;
  showClearIcon?: boolean;
  onClearIconClick?: () => void;
  ref?: Ref<PopoverMenuMethods>;
}

const PopoverMenu = ({
  title,
  open,
  onOpen,
  onClose,
  children,
  className,
  titleClass,
  offset = 0,
  placement,
  contentWrapperClassName,
  contentStyle,
  portal,
  disabled,
  showClearIcon,
  onClearIconClick,
  ref,
  ...props
}: IPopoverMenu) => {
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState(false);

  const onOpenChange = (_open: boolean) => {
    if (_open) {
      if (onOpen) onOpen();
      setShow(_open);
    } else {
      if (onClose) onClose();
      setTimeout(() => {
        setShow(_open);
      }, 500);
    }
    setIsOpen(_open);
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    refs,
    floatingStyles,
    context,
    placement: computedPlacement,
  } = useFloating({
    open: isOpen,
    strategy: 'fixed',
    transform: false,
    onOpenChange: onOpenChange,
    placement: isMobile ? 'bottom' : placement,
    middleware: [_offset(offset), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss, role]);

  useEffect(() => {
    if (open !== undefined) onOpenChange(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (ref && typeof ref === 'object') {
    ref.current = { open: () => onOpenChange(true), close: () => onOpenChange(false) };
  }

  const translate = {
    top: { translateY: 5 },
    bottom: { translateY: -5 },
  }[computedPlacement.includes('-') ? computedPlacement.split('-')[0] : computedPlacement];

  const content = show ? (
    <FloatingFocusManager context={context} modal={false}>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className={classNames('content-wrapper z-10 w-max focus:outline-none', contentWrapperClassName)}
      >
        {show && (
          <motion.div
            className={classNames(
              'content left-0 mr-1 mb-4 w-fit origin-top transform rounded-md bg-white shadow-sm focus:outline-none',
              'flex max-h-96 flex-col overflow-hidden',
            )}
            data-lenis-prevent
            tabIndex={-1}
            variants={{
              closed: {
                opacity: 0,
                ...translate,
              },
              open: {
                opacity: 1,
                translateX: 0,
                translateY: 0,
                transition: {
                  duration: 0.4,
                },
              },
            }}
            initial="closed"
            exit="closed"
            animate={isOpen ? 'open' : 'closed'}
            style={contentStyle}
          >
            {children}
          </motion.div>
        )}
      </div>
    </FloatingFocusManager>
  ) : null;

  return (
    <div className={classNames('relative', isOpen && 'open', className, computedPlacement)} {...props}>
      <button
        type="button"
        className="main-button flex w-full cursor-pointer items-center justify-between transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50"
        ref={refs.setReference}
        disabled={disabled}
        {...getReferenceProps()}
      >
        <span
          className={classNames('popover-title', titleClass)}
          dangerouslySetInnerHTML={{
            __html: title?.replace ? title.replace(/<(p)>/g, '').replace(/<\/(p)>/g, '') : title,
          }}
        />
        <div className="arrow-container ml-3 flex items-center gap-2">
          {showClearIcon && (
            <span
              className="cursor-pointer text-lg"
              onClick={(e) => {
                e.stopPropagation();
                onClearIconClick?.();
              }}
            >
              ✕
            </span>
          )}
          <span className="arrow">
            <DropdownArrow
              role="presentation"
              className={classNames(
                'h-3.5 w-3.5 fill-black transition',
                isOpen
                  ? computedPlacement === 'top'
                    ? 'rotate-0'
                    : '-rotate-180'
                  : computedPlacement === 'top'
                    ? 'rotate-0'
                    : 'rotate-0',
              )}
            />
          </span>
        </div>
      </button>
      {portal ? <FloatingPortal id="modal-portal">{content}</FloatingPortal> : content}
    </div>
  );
};

export default PopoverMenu;
